import {useId, useRef} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {usePersonalizedHomepageImpression} from "../../hooks/use_personalized_homepage_impression";
import {IPersonalizedPropertyCard} from "../../types/IPersonalizedHomepage";
import {PersonalizedHomepageRecentlyViewedTile} from "./PersonalizedHomepageRecentlyViewedTile";

interface IProps {
    properties: IPersonalizedPropertyCard[];
    isLoading?: boolean;
    hasError?: boolean;
    emptyStateHref: string;
    onView?: (items: {property: IPersonalizedPropertyCard; index: number}[]) => void;
    onPropertyClick?: (property: IPersonalizedPropertyCard, index: number) => void;
}

const RECENTLY_VIEWED_SKELETON_TILES = [0, 1, 2, 3];
/** Fallback when the carousel has no tile to measure: tile width (341) + desktop gap (8). */
const TILE_SCROLL_STEP_FALLBACK_PX = 349;

export const PersonalizedHomepageRecentlyViewedCard = (props: IProps) => {
    const {properties, isLoading = false, hasError = false, emptyStateHref, onView, onPropertyClick} = props;
    const headingId = useId();
    const carouselRef = useRef<HTMLUListElement>(null);
    const isEmpty = !isLoading && (hasError || properties.length === 0);
    const sectionRef = usePersonalizedHomepageImpression<HTMLElement>({
        isEnabled: !isLoading && !isEmpty && !!onView,
        onImpression: () => {
            const carousel = carouselRef.current;

            if (!carousel) {
                return;
            }

            const bounds = carousel.getBoundingClientRect();
            const visibleLeft = Math.max(bounds.left, 0);
            const visibleRight = Math.min(bounds.right, window.innerWidth);
            const visibleItems = Array.from(carousel.children).flatMap((tile, index) => {
                const tileBounds = tile.getBoundingClientRect();
                const visibleWidth = Math.min(tileBounds.right, visibleRight) - Math.max(tileBounds.left, visibleLeft);
                const property = properties[index];

                // Count the offer only when at least half of its tile is visible in the carousel.
                return property && tileBounds.width > 0 && visibleWidth >= tileBounds.width / 2 ? [{property, index}] : [];
            });

            if (visibleItems.length > 0) {
                onView?.(visibleItems);
            }
        }
    });

    const scrollCarousel = (direction: 1 | -1) => {
        const carousel = carouselRef.current;

        if (!carousel) {
            return;
        }

        const firstTile = carousel.querySelector("li");
        const gap = Number.parseFloat(getComputedStyle(carousel).columnGap) || 0;
        const step = firstTile ? firstTile.getBoundingClientRect().width + gap : TILE_SCROLL_STEP_FALLBACK_PX;
        // iOS Safari bounces a smooth scroll past the edge of a mandatory-snap container to an unrelated snap point.
        const targetScrollLeft = Math.min(Math.max(carousel.scrollLeft + direction * step, 0), carousel.scrollWidth - carousel.clientWidth);

        carousel.scrollTo({left: targetScrollLeft, behavior: "smooth"});
    };

    return (
        <section className={cardStyle} ref={sectionRef} aria-labelledby={headingId} aria-busy={isLoading}>
            <header className={headStyle}>
                <Text id={headingId} as="h2" variant="headline_6" className={headingStyle}>
                    Ostatnio oglądane oferty
                </Text>

                {!isEmpty ? (
                    <div className={navigationStyle}>
                        <button type="button" className={navigationButtonStyle} onClick={() => scrollCarousel(-1)} aria-label="Poprzednie oferty">
                            <ChevronLeftIcon size="1.6" fill="var(--colors-secondary)" />
                        </button>
                        <button type="button" className={navigationButtonStyle} onClick={() => scrollCarousel(1)} aria-label="Następne oferty">
                            <ChevronRightIcon size="1.6" fill="var(--colors-secondary)" />
                        </button>
                    </div>
                ) : null}
            </header>

            {isEmpty ? (
                <div className={emptyStateStyle} role="status">
                    <Text as="p" variant="body_copy_2" className={emptyStateTextStyle}>
                        {hasError
                            ? "Nie udało się wczytać ostatnio oglądanych ofert."
                            : "Mieszkania, które obejrzysz, pojawią się tutaj — wrócisz do nich jednym kliknięciem."}
                    </Text>
                    <Button variant="outlined_secondary" size="small" href={emptyStateHref}>
                        Przejdź do ofert
                    </Button>
                </div>
            ) : (
                <ul className={carouselStyle} ref={carouselRef}>
                    {isLoading
                        ? RECENTLY_VIEWED_SKELETON_TILES.map((tile) => (
                              <li key={tile} className={carouselItemStyle}>
                                  <span className={skeletonTileStyle} />
                              </li>
                          ))
                        : properties.map((property, index) => (
                              <li key={property.id} className={carouselItemStyle}>
                                  <PersonalizedHomepageRecentlyViewedTile property={property} index={index} onPropertyClick={onPropertyClick} />
                              </li>
                          ))}
                </ul>
            )}
        </section>
    );
};

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    boxSizing: "border-box",
    padding: 2,
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,

    md: {
        padding: 3
    }
});

const headStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2
});

const headingStyle = css({
    margin: 0,
    color: "var(--colors-secondary)"
});

const navigationStyle = css({
    display: "flex",
    flex: "0 0 auto",
    gap: 1
});

const navigationButtonStyle = css({
    display: "flex",
    width: "3.2rem",
    height: "3.2rem",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    backgroundColor: "var(--colors-primary)",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",

    // Touch screens keep :hover active after a tap — the pressed arrow would stay dark until the next tap elsewhere.
    "@media (hover: hover)": {
        _hover: {
            backgroundColor: "var(--colors-secondary)",
            "& svg": {
                fill: "var(--colors-white)"
            }
        }
    }
});

const carouselStyle = css({
    display: "flex",
    gap: 3,
    margin: 0,
    padding: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",

    "&::-webkit-scrollbar": {
        display: "none"
    },

    md: {
        gap: 1
    }
});

/** Full tile on narrow screens — the fixed 34.1rem must not overflow the card. */
const carouselItemStyle = css({
    flex: "0 0 auto",
    width: "min(34.1rem, 100%)",
    scrollSnapAlign: "start"
});

const skeletonTileStyle = css({
    display: "block",
    width: "100%",
    height: "44.2rem",
    backgroundColor: "var(--colors-gray-200)",
    borderRadius: 2,

    md: {
        height: "41rem"
    }
});

const emptyStateStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 2,
    py: 3
});

const emptyStateTextStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)"
});
import {useId, useRef} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {usePersonalizedHomepageImpression} from "../../hooks/use_personalized_homepage_impression";
import {IPersonalizedPropertyCard} from "../../types/IPersonalizedHomepage";
import {PersonalizedHomepageRecentlyViewedTile} from "./PersonalizedHomepageRecentlyViewedTile";

interface IProps {
    properties: IPersonalizedPropertyCard[];
    isLoading?: boolean;
    hasError?: boolean;
    emptyStateHref: string;
    onView?: (items: {property: IPersonalizedPropertyCard; index: number}[]) => void;
    onPropertyClick?: (property: IPersonalizedPropertyCard, index: number) => void;
}

const RECENTLY_VIEWED_SKELETON_TILES = [0, 1, 2, 3];
/** Fallback when the carousel has no tile to measure: tile width (341) + desktop gap (8). */
const TILE_SCROLL_STEP_FALLBACK_PX = 349;

export const PersonalizedHomepageRecentlyViewedCard = (props: IProps) => {
    const {properties, isLoading = false, hasError = false, emptyStateHref, onView, onPropertyClick} = props;
    const headingId = useId();
    const carouselRef = useRef<HTMLUListElement>(null);
    const isEmpty = !isLoading && (hasError || properties.length === 0);
    const sectionRef = usePersonalizedHomepageImpression<HTMLElement>({
        isEnabled: !isLoading && !isEmpty && !!onView,
        onImpression: () => {
            const carousel = carouselRef.current;

            if (!carousel) {
                return;
            }

            const bounds = carousel.getBoundingClientRect();
            const visibleLeft = Math.max(bounds.left, 0);
            const visibleRight = Math.min(bounds.right, window.innerWidth);
            const visibleItems = Array.from(carousel.children).flatMap((tile, index) => {
                const tileBounds = tile.getBoundingClientRect();
                const visibleWidth = Math.min(tileBounds.right, visibleRight) - Math.max(tileBounds.left, visibleLeft);
                const property = properties[index];

                // Count the offer only when at least half of its tile is visible in the carousel.
                return property && tileBounds.width > 0 && visibleWidth >= tileBounds.width / 2 ? [{property, index}] : [];
            });

            if (visibleItems.length > 0) {
                onView?.(visibleItems);
            }
        }
    });

    const scrollCarousel = (direction: 1 | -1) => {
        const carousel = carouselRef.current;

        if (!carousel) {
            return;
        }

        const firstTile = carousel.querySelector("li");
        const gap = Number.parseFloat(getComputedStyle(carousel).columnGap) || 0;
        const step = firstTile ? firstTile.getBoundingClientRect().width + gap : TILE_SCROLL_STEP_FALLBACK_PX;
        // iOS Safari bounces a smooth scroll past the edge of a mandatory-snap container to an unrelated snap point.
        const targetScrollLeft = Math.min(Math.max(carousel.scrollLeft + direction * step, 0), carousel.scrollWidth - carousel.clientWidth);

        carousel.scrollTo({left: targetScrollLeft, behavior: "smooth"});
    };

    return (
        <section className={cardStyle} ref={sectionRef} aria-labelledby={headingId} aria-busy={isLoading}>
            <header className={headStyle}>
                <Text id={headingId} as="h2" variant="headline_6" className={headingStyle}>
                    Ostatnio oglądane oferty
                </Text>

                {!isEmpty ? (
                    <div className={navigationStyle}>
                        <button type="button" className={navigationButtonStyle} onClick={() => scrollCarousel(-1)} aria-label="Poprzednie oferty">
                            <ChevronLeftIcon size="1.6" fill="var(--colors-secondary)" />
                        </button>
                        <button type="button" className={navigationButtonStyle} onClick={() => scrollCarousel(1)} aria-label="Następne oferty">
                            <ChevronRightIcon size="1.6" fill="var(--colors-secondary)" />
                        </button>
                    </div>
                ) : null}
            </header>

            {isEmpty ? (
                <div className={emptyStateStyle} role="status">
                    <Text as="p" variant="body_copy_2" className={emptyStateTextStyle}>
                        {hasError
                            ? "Nie udało się wczytać ostatnio oglądanych ofert."
                            : "Mieszkania, które obejrzysz, pojawią się tutaj — wrócisz do nich jednym kliknięciem."}
                    </Text>
                    <Button variant="outlined_secondary" size="small" href={emptyStateHref}>
                        Przejdź do ofert
                    </Button>
                </div>
            ) : (
                <ul className={carouselStyle} ref={carouselRef}>
                    {isLoading
                        ? RECENTLY_VIEWED_SKELETON_TILES.map((tile) => (
                              <li key={tile} className={carouselItemStyle}>
                                  <span className={skeletonTileStyle} />
                              </li>
                          ))
                        : properties.map((property, index) => (
                              <li key={property.id} className={carouselItemStyle}>
                                  <PersonalizedHomepageRecentlyViewedTile property={property} index={index} onPropertyClick={onPropertyClick} />
                              </li>
                          ))}
                </ul>
            )}
        </section>
    );
};

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    boxSizing: "border-box",
    padding: 2,
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,

    md: {
        padding: 3
    }
});

const headStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2
});

const headingStyle = css({
    margin: 0,
    color: "var(--colors-secondary)"
});

const navigationStyle = css({
    display: "flex",
    flex: "0 0 auto",
    gap: 1
});

const navigationButtonStyle = css({
    display: "flex",
    width: "3.2rem",
    height: "3.2rem",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    backgroundColor: "var(--colors-primary)",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",

    // Touch screens keep :hover active after a tap — the pressed arrow would stay dark until the next tap elsewhere.
    "@media (hover: hover)": {
        _hover: {
            backgroundColor: "var(--colors-secondary)",
            "& svg": {
                fill: "var(--colors-white)"
            }
        }
    }
});

const carouselStyle = css({
    display: "flex",
    gap: 3,
    margin: 0,
    padding: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",

    "&::-webkit-scrollbar": {
        display: "none"
    },

    md: {
        gap: 1
    }
});

/** Full tile on narrow screens — the fixed 34.1rem must not overflow the card. */
const carouselItemStyle = css({
    flex: "0 0 auto",
    width: "min(34.1rem, 100%)",
    scrollSnapAlign: "start"
});

const skeletonTileStyle = css({
    display: "block",
    width: "100%",
    height: "44.2rem",
    backgroundColor: "var(--colors-gray-200)",
    borderRadius: 2,

    md: {
        height: "41rem"
    }
});

const emptyStateStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 2,
    py: 3
});

const emptyStateTextStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)"
});
